import * as yup from 'yup';
import { SeasonLeagueFragment } from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import SelectField from '@amf/shared/components/form/SelectField';
import { LeagueTeamHomeOrAway } from '@amf/shared/data/League';

const schema = yup.object({
  phase: yup.string(),
  homeOrAway: yup.string(),
});

export type TeamTableFilterData = {
  phase: string;
  homeOrAway: LeagueTeamHomeOrAway;
};

const defaultValues: TeamTableFilterData = {
  phase: '',
  homeOrAway: LeagueTeamHomeOrAway.all,
};

interface TeamTableFilterProps {
  league: SeasonLeagueFragment;
  onChange?: (data: TeamTableFilterData) => void;
}

function TeamTableFilter({ league, onChange }: TeamTableFilterProps) {
  const { t } = useTranslate();
  const phase = league.phases.find(phase => phase.current) ?? league.phases[0];
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TeamTableFilterData>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      phase: phase?.id,
    },
  });

  const update = useCallback(
    (data: TeamTableFilterData) => {
      if (!onChange) return;
      onChange(data);
    },
    [onChange],
  );

  watch(update);

  return (
    <div className='Filter'>
      <form onSubmit={handleSubmit(update)} className='Filter__form'>
        <Controller
          name='phase'
          control={control}
          render={({ field }) => (
            <SelectField
              className='Filter__field'
              label={t('filter.phase')}
              error={errors.phase?.message}
              {...field}
            >
              {league.phases.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Controller
          name='homeOrAway'
          control={control}
          render={({ field }) => (
            <SelectField
              className='Filter__field'
              label={t('filter.homeOrAway')}
              error={errors.homeOrAway?.message}
              {...field}
            >
              <option value={LeagueTeamHomeOrAway.all}>{t('filter.homeOrAway.empty')}</option>
              <option value={LeagueTeamHomeOrAway.home}>{t('filter.homeOrAway.home')}</option>
              <option value={LeagueTeamHomeOrAway.away}>{t('filter.homeOrAway.away')}</option>
            </SelectField>
          )}
        />
      </form>
    </div>
  );
}

export default TeamTableFilter;
